import { Text } from '@querodelivery/uni-react';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { Skeleton } from '@/_config/lib/react-native-skeleton';
import styles from './timeline.module.scss';

interface ITimelineProps {
  steps: string[];
  currentStep: number;
  isLoading?: boolean;
}

export const Timeline = ({
  steps,
  currentStep,
  isLoading = false,
}: ITimelineProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleSteps = useMemo(() => {
    if (isExpanded || steps.length <= 3) {
      return steps.map((step, index) => ({ step, originalIndex: index }));
    }

    // Lógica para mostrar apenas 3 steps quando reduzido
    const totalSteps = steps.length;
    let startIndex: number;
    let endIndex: number;

    // Se está no início: step atual + próximas 2
    if (currentStep === 0) {
      startIndex = 0;
      endIndex = Math.min(2, totalSteps - 1);
    }
    // Se está no fim: step atual + anteriores 2
    else if (currentStep >= totalSteps - 1) {
      startIndex = Math.max(0, totalSteps - 3);
      endIndex = totalSteps - 1;
    }
    // No meio: step anterior + step atual + próxima
    else {
      startIndex = currentStep - 1;
      endIndex = currentStep + 1;
    }

    return steps
      .slice(startIndex, endIndex + 1)
      .map((step, idx) => ({ step, originalIndex: startIndex + idx }));
  }, [steps, currentStep, isExpanded]);

  const shouldShowToggle = steps.length > 3;

  if (isLoading) {
    return (
      <div className={styles.container}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className={styles.stepContainer}>
            <div className={styles.dotWrapper}>
              <Skeleton circle width={14} height={14} />
              {index < 2 && (
                <div className={styles.skeletonLine}>
                  <Skeleton width={1} height={20} />
                </div>
              )}
            </div>
            <Skeleton width={120} height={16} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {visibleSteps.map(({ step, originalIndex }, displayIndex) => {
        const isPassed = originalIndex < currentStep;
        const isCurrent = originalIndex === currentStep;
        const isNext = originalIndex > currentStep;
        const isLastVisible = displayIndex === visibleSteps.length - 1;

        return (
          <div key={originalIndex} className={styles.stepContainer}>
            <div className={styles.dotWrapper}>
              <div
                className={classNames(styles.dot, {
                  [styles.passedDot]: isPassed,
                  [styles.nextDot]: isNext,
                })}
              />
              {!isLastVisible && <div className={styles.line} />}
            </div>
            <Text
              variant={isCurrent ? 'headingSm' : 'bodySm'}
              tone={isPassed || isCurrent ? 'highlight' : 'supporting'}
              fontWeight={isCurrent ? 'bold' : 'medium'}
              lineHeight="lg"
            >
              {step}
            </Text>
          </div>
        );
      })}
      {shouldShowToggle && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={styles.toggleButton}
        >
          <Text
            tone="supporting"
            fontWeight="semibold"
            fontSize="sm"
            textDecorationLine="underline"
          >
            {isExpanded ? 'Ver menos' : 'Ver mais'}
          </Text>
        </button>
      )}
    </div>
  );
};
